import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import type { History } from 'history'
import { equals, isEmpty, move, omit, path, pathOr } from 'ramda'
import { type ComponentProps, type MutableRefObject, PureComponent } from 'react'

import ConfirmationDialog from '@/app/component/atom/confirmation-dialog'
import CopyBox from '@/app/component/atom/copy-box'
import DeleteDialog from '@/app/component/atom/delete-dialog'
import EditableText from '@/app/component/atom/form/editable-text'
import CustomTooltip from '@/app/component/atom/tooltip'
import Loading from '@/app/component/guard/loading'
import FloatingSaveBarCont from '@/app/component/layout/floating-save-bar'
import IconText from '@/app/component/layout/icon-text'
import createHydrated from '@/app/component/wrapper/hydratable'
import { checkCampaignForValidationErrors } from '@/app/module/campaigns/campaign-validation-helpers'
import { CAMPAIGN_NO_CONTENT, getCampaignType, NO_SENDER_ID_TEXT } from '@/app/module/campaigns/helpers'
import type { useSaveCampaign } from '@/app/module/campaigns/hooks/use-save-campaign'
import { getMessageTypesCount, hasVoice } from '@/app/module/campaigns/message-type-helpers'
import { isReminderCampaign, reminderItemToDTO } from '@/app/module/campaigns/models/reminder/transform'
import { getCampaignTemplate, isSkippingLanguage } from '@/app/module/campaigns/template-helpers'
import {
  BackendCampaignTemplate,
  type BaseCampaignItem,
  type CampaignItem,
  type CampaignTemplate,
  CampaignTemplateType,
  type DripCampaignItem,
  type FilesState,
  type LegacyCampaignItem,
  type MessagesState,
  type MultimessageItem,
  type Part,
  type ReminderCampaignItem,
  type SMSBlastCampaignItem,
} from '@/app/module/campaigns/types'
import { fromCampaign } from '@/app/module/campaigns/utils/campaign-content'
import { generateCampaignName } from '@/app/module/campaigns/utils/transform'
import type { NotificationsData } from '@/app/module/notifications/types'
import TourContainer from '@/app/module/tour/tour.container'
import { openClearFeed } from '@/app/service/clearfeed/utils'

import CampaignDrawer from './campaign-drawer'
import { toPartErrors } from './campaign-navigator'
import CampaignSidebar from './campaign-sidebar'
import ExitConfirm from './item/exit-confirm'
import { getCampaignItem } from './item-helpers'
import ItemProvider from './item-provider'
import PublishedBanner from './published-banner'
import { getCampaignSteps } from './step-helpers'

// New campaigns contain only their type and initial variables until the steps populate them.
// Keep assertions at legacy helper/provider boundaries that require persisted campaign types.
type EditorCampaign = Partial<BaseCampaignItem> & {
  id?: number | string
  template?: BackendCampaignTemplate
  type: CampaignTemplateType
  variables: Partial<Omit<DripCampaignItem['variables'], 'parts'>> &
    Partial<Pick<ReminderCampaignItem['variables'], 'defaultArgs'>> &
    Partial<Pick<SMSBlastCampaignItem['variables'], 'message'>> & { parts?: Part[] }
}

type ValidationInput = Parameters<typeof checkCampaignForValidationErrors>[0]

type SaveCampaign = ReturnType<typeof useSaveCampaign>[0]
type SaveData = Parameters<SaveCampaign>[0]
type SaveResult = Pick<NonNullable<Awaited<ReturnType<SaveCampaign>>>, 'id' | 'published'>

export type SaveChangesRef = MutableRefObject<(() => Promise<boolean>) | null>

type Props = {
  changed: boolean
  classes?: unknown
  contacts?: unknown
  error?: ComponentProps<typeof Loading>['error']
  files: Pick<FilesState, 'loadingItems'> & Partial<FilesState>
  history: History
  item?: EditorCampaign
  itemId?: number | string
  loading: boolean
  messages: Pick<MessagesState, 'loading' | 'senderIds' | 'senderIdsForDrip' | 'whatsappSenderIds'> &
    Partial<MessagesState>
  orgId?: number | string
  saveChangesRef?: SaveChangesRef
  step?: string
  type: CampaignTemplateType

  createNotification: (data: NotificationsData) => void
  onFinish: (campaignId?: number | string) => void
  onSave: (payload: { id: number | string; tab?: string }) => void
  saveCampaign: (item: SaveData, toBePublished?: boolean) => Promise<SaveResult | undefined>
  setChanged: (changed: boolean) => void
}

type State = {
  activePart: string | undefined
  drawerOpen: boolean
  errorMessages: string[]
  isTitleEditable: boolean
  isValid: boolean
  item: EditorCampaign
  languageDialogOpen: boolean
  loading: boolean
  newItems: string[]
  phoneNumberDialogOpen: boolean | null
  showExitConfirmDialog: boolean
  step: number
}

const emptyArray: never[] = []
export class CampaignsItem extends PureComponent<Props, State> {
  state: State = {
    step: 0,
    item: getCampaignItem(this.props as Parameters<typeof getCampaignItem>[0]) as EditorCampaign,
    showExitConfirmDialog: false,
    loading: false,
    isTitleEditable: false,
    newItems: [],
    isValid: false,
    errorMessages: [],
    languageDialogOpen: false,
    drawerOpen: true,
    activePart: undefined,
    phoneNumberDialogOpen: null,
  }

  hasDrawer = () => this.state.item?.type !== 'smsblast'

  hasLanguage = () => !['smsblast'].includes(this.state.item?.type)

  hasPhoneNumber = () => {
    const { messages } = this.props
    const { item } = this.state

    if (messages.loading) {
      return true
    }

    if (['smssurvey', 'whatsappsurvey'].includes(item.type)) {
      const { senderIds, whatsappSenderIds } = messages

      if (item.type === 'smssurvey') {
        return !!senderIds.length
      }
      return !!whatsappSenderIds.length
    }

    return true
  }

  getSteps = () => {
    const { item } = this.state
    const { item: propsItem, itemId } = this.props
    const campaign = itemId && propsItem ? propsItem : item
    const { type } = campaign

    const hasLanguage = this.hasLanguage()
    const hasVoiceMessage =
      type === 'ivr' || (type === 'drip' && hasVoice(getMessageTypesCount(item as MultimessageItem)))
    const isReminder = isReminderCampaign(campaign)
    const published = pathOr(false, ['published'], item)

    return getCampaignSteps({
      hasLanguage,
      hasVoiceMessage,
      isReminder,
      published,
      type,
    })
  }

  getFirstStep = () => {
    const { steps } = this.getSteps()

    return Math.max(
      Object.keys(steps).findIndex((key) => !steps[key as `${number}`].disabled),
      0,
    )
  }

  getCurrentStep = () => {
    const { itemId } = this.props
    const { step } = this.state

    if (!itemId) {
      return step
    }

    const { steps } = this.getSteps()
    return Object.keys(steps).findIndex((key) => steps[key as `${number}`].name === this.props.step)
  }

  getPreviousStep = () => {
    const { steps } = this.getSteps()
    const step = this.getCurrentStep()

    let previousStep = step - 1
    if (!steps[previousStep]) {
      return step
    }
    while (steps[previousStep].disabled) {
      previousStep -= 1
      if (!steps[previousStep]) {
        return step
      }
    }
    return previousStep
  }

  getNextStep = (step = this.getCurrentStep()) => {
    const { steps, stepsMap } = this.getSteps()

    let nextStep = step + 1
    if (!stepsMap[nextStep]) {
      return step
    }
    while (steps[nextStep].disabled) {
      nextStep += 1
      if (!steps[nextStep]) {
        return step
      }
    }
    return nextStep
  }

  getLastStep = () => {
    const { steps } = this.getSteps()

    return Object.keys(steps).length - 1
  }

  isValidStep = (step: string) => {
    const { steps } = this.getSteps()

    return !!Object.values(steps).find((s) => !s.disabled && s.name === step)
  }

  componentDidMount() {
    if (this.props.saveChangesRef) this.props.saveChangesRef.current = this.saveBeforeExit
    const { step, itemId, history } = this.props

    if (this.state.step === this.getLastStep() || this.props.step === 'confirm') {
      this.checkForInvalidMessages(this.state.item)
    }

    const template = getCampaignTemplate(this.props.history)
    this.updateItemState({
      name: generateCampaignName(this.state.item, template as CampaignTemplate),
    })

    let firstStep = this.getFirstStep()
    const skippingLanguage = isSkippingLanguage(this.state.item.type, template as CampaignTemplate)
    if (skippingLanguage) {
      firstStep = this.getNextStep(firstStep)
    }

    if (!itemId) {
      this.setState({
        step: firstStep,
        languageDialogOpen: !skippingLanguage && this.hasLanguage(),
      })
    } else if (!step) {
      history.push(`/campaigns/edit/${itemId}/content`)
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { orgId, item, itemId, history, step, type, loading } = this.props
    if (prevProps.orgId && !isEmpty(prevProps.orgId) && prevProps.orgId !== orgId) {
      history.push('/campaigns?page=1')
    }
    // for EDIT cases where the item's request gets received after mount
    if (item?.type && (!prevProps.item || isEmpty(prevProps.item) || prevProps.item.id !== item.id)) {
      this.setState({
        item,
      })
    }

    const firstPart = this.state.item?.variables?.parts?.[0]
    if (!this.state.activePart && firstPart && !pathOr(false, ['published'], this.state.item)) {
      this.setState({
        activePart: firstPart.id,
      })
    }

    if (
      step === 'confirm' &&
      item?.type &&
      (!prevProps.item || isEmpty(prevProps.item) || prevProps.item.id !== item.id)
    ) {
      this.checkForInvalidMessages(item)
    }

    if (type && prevProps.type !== type) {
      this.updateItemState({
        type,
      })
    }

    if (!loading && step && !this.isValidStep(step)) {
      const firstStep = this.getFirstStep()
      if (!itemId) {
        this.setState({
          step: firstStep,
        })
      } else {
        history.push(`/campaigns/edit/${itemId}/content`)
      }
    }

    if (!itemId && this.state.step === this.getFirstStep() && !equals(this.props.messages, prevProps.messages)) {
      this.setState({
        phoneNumberDialogOpen: !this.hasPhoneNumber(),
      })
    }
  }

  componentWillUnmount() {
    if (this.props.saveChangesRef) this.props.saveChangesRef.current = null
  }

  checkForInvalidMessages = (item: EditorCampaign) => {
    const { senderIdsForDrip } = pathOr([], ['messages'], this.props) as unknown as Pick<
      MessagesState,
      'senderIdsForDrip'
    >
    const { isValid, errorMessages } = checkCampaignForValidationErrors({
      item: item as ValidationInput['item'],
      contacts: (item.contacts || {}) as ValidationInput['contacts'],
      senderIdsForDrip,
    })
    this.setState({
      isValid,
      errorMessages,
    })
  }

  save({ item, toBePublished }: { item: SaveData; toBePublished: boolean }) {
    const { saveCampaign } = this.props

    return saveCampaign(item, toBePublished)
  }

  goToStep(index: number) {
    const { itemId, history } = this.props
    const { stepsMap } = this.getSteps()
    if (index === this.getLastStep()) {
      this.checkForInvalidMessages(this.state.item)
    }
    this.setState({
      step: index,
    })

    if (itemId) {
      history.push(`/campaigns/edit/${itemId}/${stepsMap[index]}`)
    }
  }

  setLoading = (loading: boolean) => {
    this.setState({
      loading,
    })
  }

  toggleTitleEdit = () => {
    this.setState({
      isTitleEditable: !this.state.isTitleEditable,
    })
  }

  addNewItem = ({ id }: Pick<Part, 'id'>) => {
    this.setState({
      activePart: id,
      newItems: [...this.state.newItems, id],
    })
  }

  updateItemState = (partialItem: Partial<EditorCampaign>) => {
    const { item } = this.state
    const newItem = {
      ...item,
      ...partialItem,
    }

    this.setState({
      item: newItem,
    })
  }

  markChanged = () => {
    const { changed, setChanged } = this.props
    if (!changed) {
      setChanged(true)
    }
  }

  transformItem = () => {
    const { item } = this.state
    if (isReminderCampaign(item)) {
      return reminderItemToDTO(item)
    }
    return fromCampaign(item as LegacyCampaignItem, getCampaignTemplate(this.props.history) as CampaignTemplate)
  }

  transformItemForLaunch = () => {
    const { item } = this.state
    const launchNowItem = {
      ...item,
      launch: {
        ...item.launch,
        immediate: true,
      },
    }

    if (isReminderCampaign(launchNowItem)) {
      return reminderItemToDTO(launchNowItem)
    }
    return fromCampaign(
      launchNowItem as LegacyCampaignItem,
      getCampaignTemplate(this.props.history) as CampaignTemplate,
    )
  }

  onSaveOrLaunch = (activeStep: number) => {
    const { onFinish, onSave, setChanged } = this.props

    const { stepsMap } = this.getSteps()
    const lastStep = this.getLastStep()
    if (activeStep === lastStep) {
      const item = this.transformItemForLaunch()
      this.save({
        item,
        toBePublished: true,
      })
        .then(async (res) => {
          if (!res) {
            return undefined
          }
          setChanged(false)
          onSave({ id: res.id, tab: stepsMap[lastStep] })

          // publishing the campaign should be handled in saveCampaign prop
          return res
        })
        .then((res) => {
          if (!res) {
            return
          }
          if (res.published) {
            onFinish(res.id)
          }
          if (!res.published && res.id) {
            onSave({ id: res.id })
          }
        })
    } else {
      this.goToStep(this.getNextStep())
    }
  }

  onItemChange = (newItem: EditorCampaign) => {
    const { history } = this.props
    if (equals(newItem, this.state.item)) {
      return
    }

    if (
      newItem?.variables?.senderIdQuestions !== this.state.item?.variables?.senderIdQuestions ||
      newItem?.variables?.senderIdReplies !== this.state.item?.variables?.senderIdReplies ||
      newItem?.variables?.senderIdWhatsapp !== this.state.item?.variables?.senderIdWhatsapp ||
      !equals(newItem?.variables?.callerIds, this.state.item?.variables?.callerIds) ||
      newItem?.variables?.senderId !== this.state.item?.variables?.senderId
    ) {
      this.checkForInvalidMessages(newItem)
    }
    if (path(['id'], newItem) === path(['id'], this.state.item)) {
      const type = this.state.item.type || this.props.type
      this.updateItemState({
        ...newItem,
        name: generateCampaignName(
          { name: this.state.item.name, type },
          getCampaignTemplate(history) as CampaignTemplate,
        ),
        type,
      })
      this.markChanged()
    }
  }

  setActivePart = (partId: string) => {
    const { stepsMap } = this.getSteps()
    const contentStep = stepsMap.indexOf('content')

    this.setState({
      activePart: partId,
    })

    if (contentStep !== -1 && this.getCurrentStep() !== contentStep) {
      this.goToStep(contentStep)
    }
  }

  handleReorder = (fromIndex: number, toIndex: number) => {
    const { item } = this.state
    const { parts } = item.variables
    if (!parts?.length) {
      return
    }

    this.updateItemState({
      variables: {
        ...item.variables,
        parts: move(fromIndex, toIndex, parts),
      },
    })
    this.markChanged()
  }

  onPhoneNumberDialogClose = () => {
    const nextStep = this.getNextStep()
    this.goToStep(nextStep)
    this.setState({
      phoneNumberDialogOpen: false,
    })
  }

  getDrawerRight = (name: string, errorMessages: string[]) => {
    switch (name) {
      case 'content': {
        if (errorMessages.includes(CAMPAIGN_NO_CONTENT)) {
          return (
            <CustomTooltip title={CAMPAIGN_NO_CONTENT}>
              <div>
                <IconButton disabled>
                  <Icon color="warning">warning_amber</Icon>
                </IconButton>
              </div>
            </CustomTooltip>
          )
        }

        break
      }

      case 'number': {
        if (errorMessages.includes(NO_SENDER_ID_TEXT)) {
          return (
            <CustomTooltip title={NO_SENDER_ID_TEXT}>
              <div>
                <IconButton disabled>
                  <Icon color="warning">warning_amber</Icon>
                </IconButton>
              </div>
            </CustomTooltip>
          )
        }

        break
      }

      case 'confirm': {
        if (!pathOr(false, ['published'], this.state.item)) {
          break
        }

        if (errorMessages.length > 0) {
          return (
            <CustomTooltip title="You have validation errors to fix before saving updates">
              <div>
                <IconButton disabled data-testid="save-updates-indicator-error">
                  <Icon color="warning">warning_amber</Icon>
                </IconButton>
              </div>
            </CustomTooltip>
          )
        }

        break
      }

      default:
        break
    }

    return undefined
  }

  saveChanges = async () => {
    this.setLoading(true)
    try {
      const item = this.transformItem()
      const result = await this.save({ item, toBePublished: false })
      if (result) {
        if (this.props.changed) this.props.setChanged(false)
        this.setState({ newItems: [] })
      }
      return result
    } finally {
      this.setLoading(false)
    }
  }

  saveBeforeExit = async () => {
    let message: string | undefined
    if (this.props.files.loadingItems.length > 0) {
      message = 'Files are still uploading. Please wait for them to finish, then try Save and Exit again.'
    } else if (this.props.loading || this.state.loading) {
      message = 'The campaign is still updating. Please wait a moment, then try Save and Exit again.'
    } else if (
      this.state.item.published &&
      !checkCampaignForValidationErrors({
        item: this.state.item as ValidationInput['item'],
        contacts: (this.state.item.contacts || {}) as ValidationInput['contacts'],
        senderIdsForDrip: this.props.messages?.senderIdsForDrip || emptyArray,
      }).isValid
    ) {
      message = 'This campaign has validation errors. Please correct them before saving and exiting.'
    }
    if (message) {
      this.props.createNotification({ 'save-campaign': { type: 'warning', message } })
      return false
    }
    return !!(await this.saveChanges())
  }

  handleSave = async () => {
    const { itemId, onFinish, onSave } = this.props
    const { stepsMap } = this.getSteps()
    const result = await this.saveChanges()
    if (!result) return
    if (!itemId && result.id) {
      onSave({ id: result.id, tab: stepsMap[this.state.step] })
    }
    if (result.published) onFinish(result.id)
  }

  isCampaignTypeValid = () => {
    const { item, itemId, loading } = this.props
    const isDefaultItem = item && !item.type // if there's item but no type, the UI hasn't loaded yet since item was initialized as { type: undefined }
    const isTypeValid = item?.type && Object.values(CampaignTemplateType).includes(item.type) // for non-class campaigns, should be removed when all campaigns are classes
    const isTemplateValid = item?.template && Object.values(BackendCampaignTemplate).includes(item.template)

    return loading || !itemId || isDefaultItem || isTypeValid || isTemplateValid
  }

  backToCampaigns = () => this.props.history.push('/campaigns')

  render() {
    const { error, loading, itemId, onFinish, files } = this.props
    const { loadingItems } = files

    const { steps, stepsMap } = this.getSteps()
    const activeStep = itemId ? Math.max(0, this.getCurrentStep()) : this.state.step

    const published = pathOr(false, ['published'], this.state.item)
    const { saveButtonLabel, submitButtonLabel, Component } = steps[activeStep]

    const isLoading = loading

    const campaignType = getCampaignType(this.state.item)
    const campaignName = pathOr('', ['name'], this.state.item)

    const isFirstStep = activeStep === this.getFirstStep()
    const isLastStep = activeStep === this.getLastStep()

    const { drawerOpen } = this.state

    const isCampaignTypeValid = this.isCampaignTypeValid()

    const isSidebarSaveDisabled = this.state.loading || loadingItems.length > 0
    const senderIdsForDrip = this.props.messages?.senderIdsForDrip || emptyArray
    const parts = this.state.item?.variables?.parts || emptyArray
    const { errorMessages, isValid } = checkCampaignForValidationErrors({
      item: this.state.item as ValidationInput['item'],
      contacts: (this.state.item.contacts || {}) as ValidationInput['contacts'],
      senderIdsForDrip,
    })
    const errorsByPart = toPartErrors(parts, errorMessages)

    return (
      <Loading isLoading={isLoading} error={error}>
        {!isCampaignTypeValid && (
          <ConfirmationDialog
            isOpen
            cancelButtonText=""
            hideCancel
            deleteButtonText="Back to campaigns"
            text="This campaign type is not yet supported."
            title="Campaign not supported"
            onClose={this.backToCampaigns}
            onConfirm={this.backToCampaigns}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            height: 'calc(100vh - 64px)',
            justifyContent: 'flex-start',
          }}
        >
          <CampaignSidebar
            campaignName={campaignName}
            footer={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: (theme) => theme.spacing(1),
                }}
              >
                {!published && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <CustomTooltip title={isSidebarSaveDisabled ? 'Saving is temporarily unavailable' : ''}>
                      <span>
                        <Button
                          id="campaign-item-save"
                          variant="outlined"
                          color="primary"
                          disabled={isSidebarSaveDisabled}
                          startIcon={<Icon>save</Icon>}
                          onClick={this.handleSave}
                        >
                          {saveButtonLabel}
                        </Button>
                      </span>
                    </CustomTooltip>
                  </Box>
                )}
                {this.props.changed && published && (
                  <Box
                    data-testid="campaign-save-reminder"
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      pt: 0,
                      textAlign: 'center',
                    }}
                  >
                    <Icon color="warning" data-testid="campaign-save-reminder-arrow" sx={{ fontSize: 20, mb: 0.5 }}>
                      north
                    </Icon>
                    <Typography variant="caption" color="warning.main">
                      Save your changes in the Save Updates tab.
                    </Typography>
                  </Box>
                )}
              </Box>
            }
            options={Object.keys(steps)
              .filter((key) => !steps[key as `${number}`].disabled)
              .map((key) => {
                const i = Number(key)
                const { icon, name, title } = steps[i]
                return {
                  active: i === activeStep,
                  highlight: published && this.props.changed && name === 'confirm',
                  icon,
                  label: title,
                  name,
                  right: this.getDrawerRight(name, errorMessages),
                  onClick: () => this.goToStep(i),
                }
              })}
            onBack={this.backToCampaigns}
          />
          <Box
            id="scrollable-area"
            sx={{
              flex: 1,
              height: { md: 'calc(100vh - 64px)' },
              margin: 'auto',
              minHeight: 0,
              minWidth: 0,
              overflowY: 'auto',
              padding: '0 1rem',
              position: 'relative',
              width: '100%',

              transition: (theme) =>
                !drawerOpen
                  ? theme.transitions.create('margin', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    })
                  : theme.transitions.create('margin', {
                      easing: theme.transitions.easing.easeOut,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
              marginRight: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <PageTitle>
                <Typography component="div" variant="h6" color="textSecondary">
                  <EditableText
                    maxLength={200}
                    displayText={`${itemId ? 'Edit' : 'Create'} ${campaignType} "${campaignName}"`}
                    value={campaignName}
                    onChange={(name) => {
                      this.updateItemState({
                        name,
                      })
                      this.markChanged()
                    }}
                  />
                </Typography>
              </PageTitle>
              {itemId && <CopyBox text={itemId} title="Campaign ID" />}
            </div>
            <Card style={{ margin: '2rem 0' }}>
              <ExitConfirm
                isOpen={this.state.showExitConfirmDialog}
                onSave={() => {
                  this.setState({ showExitConfirmDialog: false })
                  const item = this.transformItem()
                  this.save({ item, toBePublished: false }).then((res) => {
                    if (!res) {
                      return
                    }
                    onFinish(res.published ? res.id : undefined)
                  })
                }}
                onExit={() => {
                  this.setState({ showExitConfirmDialog: false })
                  onFinish()
                }}
                onClose={() => {
                  this.setState({ showExitConfirmDialog: false })
                }}
              />
              {published && <PublishedBanner />}
            </Card>
            <ItemProvider item={this.state.item as CampaignItem} setItem={this.onItemChange}>
              <Component
                {...omit(['classes'], this.props)}
                activePart={this.state.activePart}
                errorMessages={errorMessages}
                item={this.state.item}
                isValid={isValid}
                newItems={this.state.newItems}
                published={published}
                template={getCampaignTemplate(this.props.history)}
                type={this.state.item.type}
                addNewItem={this.addNewItem}
                onChange={this.onItemChange}
                setActivePart={this.setActivePart}
                setLoading={this.setLoading}
              />
            </ItemProvider>
            <FloatingSaveBarCont>
              <Box
                sx={{
                  display: 'flex',
                  flex: '1',
                  flexDirection: 'row-reverse',
                  justifyContent: 'space-between',
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                {!isLastStep && (
                  <Button
                    id="campaign-item-next"
                    data-testid="campaign-item-next"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      this.goToStep(this.getNextStep())
                    }}
                  >
                    <span>{submitButtonLabel}</span>
                  </Button>
                )}
                {isLastStep && (
                  <>
                    {!published && (
                      <label htmlFor="submit">
                        <Button
                          component="div"
                          id="campaign-item-confirm"
                          data-testid="campaign-item-confirm"
                          variant="contained"
                          color="primary"
                          disabled={!this.state.isValid || this.state.loading}
                          onClick={() => this.onSaveOrLaunch(activeStep)}
                        >
                          {submitButtonLabel}
                        </Button>
                      </label>
                    )}

                    {published && (
                      <CustomTooltip
                        title={`${!this.state.isValid ? 'You have to fix the errors before you can save' : ''}`}
                        open={!this.state.isValid}
                      >
                        <Button
                          id="campaign-item-save"
                          data-testid="campaign-item-save"
                          variant="outlined"
                          color="primary"
                          disabled={this.state.loading || !this.state.isValid || loadingItems.length > 0}
                          onClick={this.handleSave}
                        >
                          <IconText>
                            <Icon>save</Icon>
                            <span>{saveButtonLabel}</span>
                          </IconText>
                        </Button>
                      </CustomTooltip>
                    )}
                  </>
                )}
                {!isFirstStep && (
                  <Button
                    id="campaign-item-back"
                    data-testid="campaign-item-back"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      this.goToStep(this.getPreviousStep())
                    }}
                  >
                    <span>Back</span>
                  </Button>
                )}
              </Box>
            </FloatingSaveBarCont>
          </Box>

          {this.hasDrawer() && (
            <CampaignDrawer
              activePart={this.state.activePart}
              canReorder={!published}
              errorsByPart={errorsByPart}
              open={drawerOpen}
              parts={parts}
              onClose={() => this.setState({ drawerOpen: false })}
              onOpen={() => this.setState({ drawerOpen: true })}
              onPartClick={this.setActivePart}
              onPartReorder={this.handleReorder}
            />
          )}
        </Box>
        <DeleteDialog
          cancelButtonText="No, skip languages"
          deleteButtonText="Yes, configure languages"
          icon="language"
          isOpen={stepsMap[this.state.step] === 'languages' && this.state.languageDialogOpen}
          text={
            <>
              Will your messages be in <b>multiple languages</b> in this campaign?
            </>
          }
          title="Language Setup"
          onClose={() => {
            this.setState({ languageDialogOpen: false })
            const nextStep = this.getNextStep()
            this.goToStep(nextStep)
          }}
          onConfirm={() => this.setState({ languageDialogOpen: false })}
        />
        <DeleteDialog
          cancelButtonText="I’ll do this later"
          deleteButtonText="Chat with Support"
          icon="phone"
          isOpen={stepsMap[this.state.step] === 'number' && !!this.state.phoneNumberDialogOpen}
          text={
            <>
              To launch an {this.state.item.type === 'smssurvey' ? 'SMS' : 'WhatsApp'} survey, you need a two-way phone
              number. Please reach out to our support team to procure one for you.
            </>
          }
          title="Missing Phone Number"
          onClose={this.onPhoneNumberDialogClose}
          onConfirm={() => {
            openClearFeed()
            this.onPhoneNumberDialogClose()
          }}
        />
        {!this.props.itemId && <TourContainer type={this.props.type} />}
      </Loading>
    )
  }
}
const PageTitle = styled('div')(() => ({
  width: '100%',
  marginTop: '2rem',
}))

export default createHydrated(CampaignsItem)
